/*
 * Copyright (c) 2025. Sergey S. Chernov - All Rights Reserved
 *
 *  You may use, distribute and modify this code under the
 * terms of the private license, which you must obtain from the author
 *
 * To obtain the license, contact the author: https://t.me/real_sergeych or email to
 * real dot sergeych at gmail.
 */

package net.sergeych.crypto2

import com.ionspin.kotlin.crypto.box.Box
import com.ionspin.kotlin.crypto.box.crypto_box_SEEDBYTES
import com.ionspin.kotlin.crypto.scalarmult.ScalarMultiplication
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.Transient

@Deprecated("Use DecryptingSecretKey instead",ReplaceWith("DecryptingSecretKey"))
typealias SecretKey = DecryptingSecretKey

/**
 * The secret key used in public-key encryption; it is used to _decrypt_ data encrypted with its
 * public counterpart, see [EncryptingPublicKey].
 */
@Serializable
@SerialName("encs")
class DecryptingSecretKey(
    override val keyBytes: UByteArray,
    @Transient
    val _cachedPublicKey: EncryptingPublicKey? = null,
) : DecryptingKey, UniversalKey() {

    @Transient
    override val label: String = "sec"

    /**
     * Decrypt with authentication checks the message which must have [Asymmetric.Message.senderPublicKey] set.
     * Use [decryptWithSenderKey] otherwise. Note that the authenticated encryption is always use, even if
     * the [EncryptingPublicKey.encryptAnonymousMessage] was used to create a message, if it is successfully decrypted,
     * it is guaranteed that the message was not altered after creation.
     *
     * @throws DecryptionFailedException If the message is tampered (changed after creation) or was not intended for us,
     */
    fun decrypt(message: Asymmetric.Message): UByteArray = message.decrypt(this)


    /**
     * Decrypt using [senderPublicKey] as a sender key (overriding the [Asymmetric.Message.senderPublicKey] if set).
     * See [decrypt] for more.
     */
    fun decryptWithSenderKey(message: Asymmetric.Message, senderPublicKey: EncryptingPublicKey): UByteArray =
        message.decryptWithSenderKey(senderPublicKey, this)

    @Transient
    private var cachedPublicKey: EncryptingPublicKey? = _cachedPublicKey

    /**
     * The corresponding public key
     */
    val publicKey: EncryptingPublicKey by lazy {
        if (cachedPublicKey != null)
            cachedPublicKey!!
        else
            EncryptingPublicKey(ScalarMultiplication.scalarMultiplicationBase(keyBytes))
                .also { cachedPublicKey = it }
    }

    /**
     * Nonce-based decryption is impossible, it is already included in message
     */
    override fun decryptWithNonce(cipherData: UByteArray, nonce: UByteArray): UByteArray = decrypt(cipherData)

    /**
     * Decrypt without a nonce as edwards curve decryption does not need it
     */
    override fun decrypt(cipherData: UByteArray): UByteArray {
        return Asymmetric.Message.decode(cipherData).decrypt(this)
    }

    override val magic: KeysmagicNumber = KeysmagicNumber.defaultAssymmetric
    override val id: KeyId by lazy { publicKey.id }

    override val nonceBytesLength: Int
        get() = 0

    companion object {
        data class KeyPair(val secretKey: DecryptingSecretKey, val publicKey: EncryptingPublicKey)

        /**
         * Required seed size for deterministic decrypting key generation.
         */
        val seedLength = crypto_box_SEEDBYTES

        /**
         * Generate a new random pair of public and secret keys.
         */
        fun generateKeys(): KeyPair {
            val p = Box.keypair()
            val pk = EncryptingPublicKey(p.publicKey)
            return KeyPair(DecryptingSecretKey(p.secretKey, pk), pk)
        }

        /**
         * Generate the key pair deterministically from an exact [seedLength]-byte seed.
         */
        fun generateKeys(seed: UByteArray): KeyPair {
            require(seed.size == seedLength) { "seed should be $seedLength bytes" }
            val p = Box.seedKeypair(seed)
            val pk = EncryptingPublicKey(p.publicKey)
            return KeyPair(DecryptingSecretKey(p.secretKey, pk), pk)
        }

        fun new(): DecryptingSecretKey = generateKeys().secretKey

        /**
         * Create a deterministic secret key from an exact [seedLength]-byte seed.
         */
        fun fromSeed(seed: UByteArray): DecryptingSecretKey = generateKeys(seed).secretKey

        /**
         * Derive an exact [seedLength]-byte seed from arbitrary byte material.
         *
         * Use this only with high-entropy source bytes. It is not a password KDF.
         */
        fun seedFromBytes(source: UByteArray): UByteArray =
            KeySeed.fromBytes(source, seedLength, "DecryptingSecretKey")

    }
}
